// apps/nos/client/src/hooks/useIngredientSearch.ts
import { useEffect } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  fetchIngredientFamilies,
  fetchIngredientPage,
  fetchIngredientRoleCounts,
} from "@/services/ingredient-search_api";
import { useDebouncedValue } from "./useSidebarFlavors";
import type { IngredientFilters } from "@/utils/types/ingredient";
import { registerServerLens } from "@/utils/serverCompounds";

// ── Query keys ────────────────────────────────────────────────────────────

export const ingredientSearchKeys = {
  all: (flavorId: string) => ["ingredient-search", flavorId] as const,
  page: (
    flavorId: string,
    filters: IngredientFilters,
    versionId?: number | null,
    previewId?: string | null,
  ) =>
    ["ingredient-search", flavorId, "page", filters, versionId, previewId] as const,
  families: (flavorId: string) =>
    ["ingredient-search", flavorId, "families"] as const,
  roleCounts: (flavorId: string, filters: IngredientFilters) =>
    ["ingredient-search", flavorId, "role-counts", filters] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────

// Each queryFn destructures the `signal` React Query provides and forwards
// it to the fetcher. When the queryKey changes (filters typed, kind toggled,
// etc.) or the component unmounts, React Query aborts the signal and the
// in-flight HTTP request is closed at the TCP level. Without this, stale
// keystroke-fired requests pile up on the server (visible in the perf logs
// as concurrent /ingredient-search?q=112- and ?q=112-19-6 both running to
// completion).

export function useIngredientSearch(
  flavorId: string | null,
  filters: IngredientFilters,
  versionId: number | null = null,
  previewId: string | null = null,
) {
  // Debounce the search box; other filters fire immediately.
  const debouncedQ = useDebouncedValue(filters.q, 250);
  const effective: IngredientFilters = { ...filters, q: debouncedQ };

  const q = useInfiniteQuery({
    queryKey: ingredientSearchKeys.page(flavorId ?? "_", effective, versionId, previewId),
    queryFn: ({ pageParam, signal }) =>
      fetchIngredientPage(flavorId!, effective, pageParam, signal, versionId, previewId),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: flavorId !== null,
    staleTime: 30_000,
    // The autosave flips `versionId` after a new flavor_formula_version
    // lands, which changes the queryKey here too. Without keepPreviousData,
    // the list goes blank (data → undefined) until the new fetch resolves
    // — the user sees the entire Add Ingredients panel flicker. With it,
    // the prior delta-loss-sorted rows stay rendered and the new ordering
    // diffs in once the freshly-computed ingredient_sort returns.
    placeholderData: keepPreviousData,
    // No maxPages — previously loaded pages stay in cache for back-scroll.
  });

  // Side-effect: prime the substance registry with lens info from every page
  // that lands. The flask reads lens from the registry, so adding a row from
  // search renders with the correct family color + lens grouping with no flash.
  useEffect(() => {
    if (!q.data) return;
    const rows = q.data.pages.flatMap((p) => p.items);
    if (rows.length === 0) return;
    registerServerLens(
      rows.map((r) => ({
        compoundId: r.compoundId,
        // Pass name/cas/kind so the registry entry created here matches what
        // useFlavorFormulas would write — no "compound #N" placeholder flash
        // when the user adds an ingredient from search.
        name: r.name,
        cas:  r.identifiers.casPrimary,
        kind: r.kind,
        lens: {
          primaryRole:    r.technicalRoles[0] ?? null,
          technicalRoles: r.technicalRoles,
          solubility:     r.solubility,
          chemClass:      r.chemClass,
          family:         r.family,
          flavorLabels:   r.flavorLabels,
        },
      })),
    );
  }, [q.data]);

  return q;
}

export function useIngredientFamilies(flavorId: string | null) {
  return useQuery({
    queryKey: ingredientSearchKeys.families(flavorId ?? "_"),
    queryFn: ({ signal }) => fetchIngredientFamilies(flavorId!, signal),
    enabled: flavorId !== null,
    staleTime: 5 * 60_000,
  });
}

export function useIngredientRoleCounts(
  flavorId: string | null,
  filters: IngredientFilters,
  versionId: number | null = null,
) {
  // Despite the hook name, this endpoint returns ALL facet counts (kind /
  // family / role) plus the top-header total. Role is included in the query
  // so the server can compute `total` matching every active filter; per-
  // facet counts internally ignore their own facet.
  //
  // Important: versionId is passed to the server (it's needed for the
  // ingredient_pool_for_version CTE that bounds visibility to the FDA +
  // cached set) but it is NOT part of the cache key. The pool depends on
  // the active workbench pin, not on the specific version — every save
  // produces a new versionId but the same pool, so the resulting facet
  // counts are byte-identical. Keying on versionId would refetch the same
  // counts after every autosave for no behavioral change.
  const debouncedQ = useDebouncedValue(filters.q, 250);
  const effective: IngredientFilters = { ...filters, q: debouncedQ };
  return useQuery({
    queryKey: ingredientSearchKeys.roleCounts(flavorId ?? "_", effective),
    queryFn: ({ signal }) =>
      fetchIngredientRoleCounts(flavorId!, effective, signal, versionId),
    enabled: flavorId !== null,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}
